import { useEffect, useId, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import './floatingOverlay.css';

type FloatingOverlayProps = {
  open: boolean;
  onClose: () => void;
  /** Accessible title for the dialog */
  title?: string;
  /** Optional subtitle under the title */
  subtitle?: string;
  children: ReactNode;
  /** Extra classes on the sheet card */
  className?: string;
  /** Max width of the sheet (default 640). Use xl for dense workspaces. */
  maxWidth?: number;
  /** Preset size — xl expands for schedule booking */
  size?: 'md' | 'xl';
  /** Show the header close button (default true) */
  showClose?: boolean;
  /** Called after Escape / backdrop — defaults to onClose */
  closeOnBackdrop?: boolean;
  /** Optional footer pinned below scroll body */
  footer?: ReactNode;
};

/**
 * Shared dimmed overlay + elevated sheet for pickers/menus that must
 * sit above the page without colliding with form layout.
 */
export function FloatingOverlay({
  open,
  onClose,
  title,
  subtitle,
  children,
  className = '',
  maxWidth,
  size = 'md',
  showClose = true,
  closeOnBackdrop = true,
  footer,
}: FloatingOverlayProps) {
  const titleId = useId();
  const resolvedMaxWidth =
    maxWidth ?? (size === 'xl' ? 1080 : 640);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={`ui-overlay ${size === 'xl' ? 'ui-overlay--xl' : ''}`.trim()}
      role="presentation"
    >
      <button
        type="button"
        className="ui-overlay__backdrop"
        aria-label="Close"
        onClick={closeOnBackdrop ? onClose : undefined}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        className={`ui-overlay__sheet ${size === 'xl' ? 'ui-overlay__sheet--xl' : ''} ${className}`.trim()}
        style={{ maxWidth: resolvedMaxWidth }}
      >
        {(title || showClose) && (
          <header className="ui-overlay__header">
            <div className="min-w-0 flex-1 text-left">
              {title ? (
                <h2
                  id={titleId}
                  className="m-0 text-[15px] font-semibold tracking-tight text-[var(--dash-ink)] sm:text-[16px]"
                >
                  {title}
                </h2>
              ) : null}
              {subtitle ? (
                <p className="mt-0.5 mb-0 text-[12px] leading-snug text-[var(--dash-muted)] sm:text-[12.5px]">
                  {subtitle}
                </p>
              ) : null}
            </div>
            {showClose ? (
              <button
                type="button"
                onClick={onClose}
                className="ui-overlay__close"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </header>
        )}
        <div className="ui-overlay__body">{children}</div>
        {footer ? <div className="ui-overlay__footer">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
